import React from 'react';
import { ChevronDown, ChevronUp, DollarSign, Calculator, AlertCircle } from 'lucide-react';

interface EconomicAnalysisStepProps {
  expandedSection: string | null;
  toggleSection: (section: string) => void;
}

export const EconomicAnalysisStep: React.FC<EconomicAnalysisStepProps> = ({
  expandedSection,
  toggleSection,
}) => {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50">
        <div className="flex items-center justify-between cursor-pointer" onClick={() => toggleSection('economics')}>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-gray-100 text-gray-700 flex items-center justify-center text-sm font-semibold">5</div>
            <h3 className="text-base font-semibold text-black">Economic Analysis & Financial Metrics</h3>
          </div>
          {expandedSection === 'economics' ? <ChevronUp className="w-4 h-4 text-gray-600" /> : <ChevronDown className="w-4 h-4 text-gray-600" />}
        </div>
      </div>
      {expandedSection === 'economics' && (
        <div className="p-6">
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              The final step calculates comprehensive financial metrics including NPV, IRR, and payback period
              using industry-standard assumptions.
            </p>

            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-xs font-semibold text-gray-700 mb-2">
                <DollarSign className="w-3 h-3 inline mr-1" />
                FINANCIAL ASSUMPTIONS
              </p>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div>
                  <p className="font-medium text-gray-900 mb-1">CAPEX Components</p>
                  <ul className="space-y-1 text-gray-600">
                    <li>• Power: $950,000/MW</li>
                    <li>• Energy: $475,000/MWh</li>
                    <li>• Installation: Included</li>
                    <li>• Grid connection: Included</li>
                  </ul>
                </div>
                <div>
                  <p className="font-medium text-gray-900 mb-1">Operating Parameters</p>
                  <ul className="space-y-1 text-gray-600">
                    <li>• O&M: 2% of CAPEX/year</li>
                    <li>• Discount rate: 8%</li>
                    <li>• Project life: 15 years</li>
                    <li>• Electricity inflation: 3%/year</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-xs font-semibold text-gray-700 mb-2">
                <Calculator className="w-3 h-3 inline mr-1" />
                NPV CALCULATION
              </p>
              <pre className="text-xs bg-white p-3 rounded border border-gray-300 overflow-x-auto">
{`NPV = -CAPEX + Σ(CFt / (1 + r)^t) for t=1 to 15

Where:
  CFt = (Savings × Degradation × Inflation) - OPEX
  
  Savings_t = Annual_Savings × (1 - 0.02)^t × (1 + 0.03)^t
  OPEX_t = CAPEX × 0.02
  r = 0.08 (discount rate)

Example (14MW/75MWh):
  CAPEX = 14×950,000 + 75×475,000 = $48,925,000
  Year 1 CF = 1,183,133×0.98×1.03 - 978,500 = $215,123
  Year 1 PV = 215,123 / 1.08 = $199,188
  ...
  NPV = -48,925,000 + Σ(PV) = -$40,234,567`}</pre>
            </div>

            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-xs font-semibold text-gray-700 mb-1">
                <AlertCircle className="w-3 h-3 inline mr-1" />
                Current Financial Challenge
              </p>
              <p className="text-xs text-gray-600">
                Despite achieving 99% of theoretical maximum savings, current CAPEX assumptions
                ($950k/MW + $475k/MWh) result in negative NPV. Market-specific cost reductions
                or policy incentives would be needed for positive returns.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};